/**
 * Tools Dashboard ROI section (from AI Garage ROI prototype)
 * Admin: program-wide ROI hero + time-saved leaderboards
 * User: Your Time Saved hero + your top tools
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Award,
  Calendar,
  Clock,
  Edit3,
  Info,
  Search,
  Shield,
  Sparkles,
  Trophy,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { getAdminPersonaScopes } from '../constants/roleAccess';
import garageAPI from '../../../services/garage/api';
import { filterDashboardUsers } from '../../../utils/dashboardUserFilters';
import { resolveUserDisplayName } from '../../../utils/userEmail';
import GarageRoiDigestModal from './GarageRoiDigestModal';
import GarageRoiReportModal from './GarageRoiReportModal';
import ToolRoiUsersModal from '../modals/ToolRoiUsersModal';
import UserToolsModal from '../modals/UserToolsModal';
import FacetDropdown from './FacetDropdown';
import GarageLoadingScreen, { GarageLoadingRegion } from './GarageLoadingScreen';
import '../AIGarage.css';
import './GarageRoiDashboardSection.css';

const AVATAR_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52C4B5'];
const getUserColor = (index) => AVATAR_COLORS[index % AVATAR_COLORS.length];
const getUserInitials = (email) => {
  if (!email) return '?';
  const name = email.split('@')[0];
  const parts = name.split('.');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const PENDING_REPORT_KEY = 'ai_garage_pending_roi_report';

const WINDOW_LABELS = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
};

const formatWindowLabel = (window, startDate, endDate) => {
  if (window === 'custom' && startDate && endDate) {
    const fmt = (d) =>
      new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  }
  return WINDOW_LABELS[window] || WINDOW_LABELS['90d'];
};

const formatHrs = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return '—';
  const hrs = Number(minutes) / 60;
  if (hrs >= 100) return `${Math.round(hrs).toLocaleString()} hrs`;
  if (hrs >= 10) return `${hrs.toFixed(1)} hrs`;
  return `${hrs.toFixed(2)} hrs`;
};

const formatNumber = (n) => (n == null ? '0' : Number(n).toLocaleString());

const SegBtn = ({ active, onClick, children }) => (
  <button
    type="button"
    className={`roi-dash-seg ${active ? 'active' : ''}`}
    onClick={onClick}
  >
    {children}
  </button>
);

const TypeBadge = ({ type }) => (
  <span className={`roi-dash-type ${(type || '').toLowerCase()}`}>{type || '—'}</span>
);

const AdminHero = ({ program, windowLabel }) => {
  if (!program) return null;
  const {
    totalMinutes = 0,
    totalHours = 0,
    totalWorkdays = 0,
    activeUsers = 0,
    configuredCount = 0,
    unconfiguredCount = 0,
    excludeAutomation = true,
    avgPerUserHours = 0,
    projectedAnnualHours = 0,
  } = program;

  return (
    <div className="roi-dash-hero-grid admin" data-tour="roi-hero">
      <div className="roi-dash-hero-main admin">
        <div className="roi-dash-hero-label">
          <Shield size={14} />
          <span>Program-wide ROI · {windowLabel}</span>
        </div>
        <div className="roi-dash-hero-metric">
          <span className="roi-dash-hero-value">{formatNumber(Math.round(totalHours))}</span>
          <span className="roi-dash-hero-unit">hrs</span>
        </div>
        <div className="roi-dash-hero-sub">
          = {formatNumber(totalMinutes)} minutes · ≈ {totalWorkdays} workdays reclaimed
        </div>
        <div className="roi-dash-hero-stats">
          <div>
            <div className="muted">Active users</div>
            <div className="stat">{formatNumber(activeUsers)}</div>
            <div className={`hint ${excludeAutomation ? 'ok' : 'warn'}`}>
              {excludeAutomation
                ? 'Automation accounts excluded ✓'
                : 'Includes automation accounts ⚠'}
            </div>
          </div>
          <div>
            <div className="muted">Configured tools</div>
            <div className="stat">{configuredCount}</div>
            <div className="hint">contributing to ROI</div>
          </div>
          <div>
            <div className="muted">Unconfigured</div>
            <div className={`stat ${unconfiguredCount > 0 ? 'warn' : ''}`}>{unconfiguredCount}</div>
            <div className="hint">need admin action</div>
          </div>
        </div>
      </div>

      <div className="roi-dash-side-card">
        <div className="roi-dash-side-label">
          <Clock size={14} />
          <span>Avg per user</span>
        </div>
        <div className="roi-dash-side-value">
          {Number(avgPerUserHours || 0).toFixed(1)}
          <span>hrs</span>
        </div>
        <div className="roi-dash-side-sub">Across {windowLabel.toLowerCase()}</div>
      </div>

      <div className="roi-dash-side-card">
        <div className="roi-dash-side-label">
          <TrendingUp size={14} />
          <span>Projected annual</span>
        </div>
        <div className="roi-dash-side-value">
          {formatNumber(Math.round(projectedAnnualHours || 0))}
          <span>hrs</span>
        </div>
        <div className="roi-dash-side-sub">At current run rate</div>
      </div>
    </div>
  );
};

const UserHero = ({ myRoi, windowLabel, onOpenDigest }) => {
  if (!myRoi || myRoi.hidden) {
    return (
      <div className="roi-dash-user-empty">
        <Info size={16} />
        <span>
          {myRoi?.reason ||
            'Your Time Saved card is unavailable for this account (automation exclusion or insufficient data).'}
        </span>
      </div>
    );
  }

  const unreported = myRoi.unreportedCount || 0;
  // Fall back to the legacy fixed-window keys so an older backend still renders.
  const timeSavedMinWindow = myRoi.timeSavedMinWindow ?? myRoi.timeSavedMin30d ?? 0;
  const sessionsWindow = myRoi.sessionsWindow ?? myRoi.sessions90d ?? 0;

  return (
    <div className="roi-dash-hero-wrap" data-tour="roi-hero">
      <div className="roi-dash-hero-grid user">
        <div className="roi-dash-hero-main user">
          <div className="roi-dash-hero-label">
            <Sparkles size={14} />
            <span>Your Time Saved</span>
          </div>
          <div className="roi-dash-user-metrics">
            <div>
              <div className="roi-dash-hero-metric">
                <span className="roi-dash-hero-value">
                  {(timeSavedMinWindow / 60).toFixed(1)}
                </span>
                <span className="roi-dash-hero-unit">hrs</span>
              </div>
              <div className="roi-dash-hero-sub">{windowLabel}</div>
            </div>
            <div>
              <div className="roi-dash-hero-metric">
                <span className="roi-dash-hero-value">
                  {(myRoi.timeSavedMinLifetime / 60).toFixed(1)}
                </span>
                <span className="roi-dash-hero-unit">hrs</span>
              </div>
              <div className="roi-dash-hero-sub">Lifetime</div>
            </div>
          </div>
          <div className="roi-dash-hero-stats">
            <div>
              <div className="muted">Rank</div>
              <div className="stat">
                #{myRoi.rank ?? '—'}{' '}
                <span className="hint inline">of {myRoi.totalUsers ?? '—'}</span>
              </div>
            </div>
            <div>
              <div className="muted">Percentile</div>
              <div className="stat">Top {myRoi.percentile ?? '—'}%</div>
            </div>
            <div>
              <div className="muted">Tools adopted</div>
              <div className="stat">
                {myRoi.toolsAdopted ?? 0}{' '}
                <span className="hint inline">
                  of {myRoi.configuredToolsTotal ?? '—'}
                </span>
              </div>
            </div>
          </div>
          <div className="roi-dash-calc-note">
            <Info size={12} />
            Declared ROI = sessions × admin min/session. Your reports calibrate estimates — they do
            not rewrite these totals.
          </div>
        </div>

        <div className="roi-dash-side-stack">
          <div className="roi-dash-side-card">
            <div className="roi-dash-side-label">
              <TrendingUp size={14} />
              <span>{windowLabel}</span>
            </div>
            <div className="roi-dash-side-value">
              {timeSavedMinWindow}
              <span>min</span>
            </div>
          </div>
          <div className="roi-dash-side-card">
            <div className="roi-dash-side-label">
              <Zap size={14} />
              <span>Sessions</span>
            </div>
            <div className="roi-dash-side-value">{sessionsWindow}</div>
            <div className="roi-dash-side-sub">Across {myRoi.toolsAdopted ?? 0} tools</div>
          </div>
        </div>
      </div>

      {unreported > 0 && !myRoi.promptOptOut && !myRoi.digestSnoozed && (
        <div className="roi-unreported-banner">
          <div>
            <strong>
              {unreported} unreported tool{unreported === 1 ? '' : 's'}
            </strong>
            <span> in the last 7 days — help calibrate time-saved estimates.</span>
          </div>
          <button type="button" onClick={onOpenDigest}>
            Review now
          </button>
        </div>
      )}
    </div>
  );
};

const UserTopTools = ({ myRoi, windowLabel, onReportTool }) => {
  const tools = myRoi?.topTools || [];
  const maxMin = Math.max(...tools.map((t) => t.minutesSaved || 0), 1);

  return (
    <div className="roi-dash-panel" data-tour="roi-user-top-tools">
      <div className="roi-dash-panel-header">
        <div className="roi-dash-panel-title">
          <Award size={16} />
          <div>
            <div className="title">Your Top Tools</div>
            <div className="subtitle">By declared time saved · {windowLabel} · pencil = report</div>
          </div>
        </div>
      </div>
      <div className="roi-dash-top-tools">
        {tools.length === 0 && (
          <div className="roi-dash-empty">
            No configured tools with sessions yet. Once admins set min/session values, your top tools will appear here.
          </div>
        )}
        {tools.map((t) => {
          const bar = Math.round(((t.minutesSaved || 0) / maxMin) * 100);
          return (
            <div key={t.toolId} className="roi-dash-top-tool">
              <div className="row">
                <span className="name">{t.name}</span>
                <div className="roi-top-tool-actions">
                  <span className="mins">{t.minutesSaved || 0}m</span>
                  <button
                    type="button"
                    className="roi-pencil-btn"
                    title="Report time saved"
                    onClick={() => onReportTool?.(t)}
                  >
                    <Edit3 size={12} />
                  </button>
                </div>
              </div>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${bar}%` }} />
              </div>
              <div className="meta">
                {t.sessions || 0} sessions
                {t.adminEstimate != null ? ` · est. ${t.adminEstimate}m` : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ToolsLeaderboard = ({
  tools,
  sortMode,
  setSortMode,
  windowLabel,
  onSelectTool,
  personaFilters,
  onTogglePersona,
  personaOptions,
  roleFilters,
  onToggleRole,
  roleOptions,
  lockedPersona = null,
}) => {
  const [search, setSearch] = useState('');

  const sorted = useMemo(() => {
    // Configured tools with activity in the selected window only (hide 0-session rows)
    const rows = [...(tools || [])].filter(
      (t) => t.roiStatus === 'configured' && (t.sessions || 0) > 0
    );
    rows.sort((a, b) => {
      if (sortMode === 'sessions') return (b.sessions || 0) - (a.sessions || 0);
      if (sortMode === 'per_session') return (b.minPerSession || 0) - (a.minPerSession || 0);
      return (b.totalMinutesSaved || 0) - (a.totalMinutesSaved || 0);
    });
    return rows;
  }, [tools, sortMode]);

  const hasPersonaFilter = personaFilters.length > 0;
  const hasRoleFilter = roleFilters.length > 0;

  // Any-match within a facet, matching Discover's filter semantics exactly:
  // a tool matches if its target personas/roles array intersects the
  // selected values at all (not just its single "primary" persona/badge).
  const applyPersonaRoleFilter = useCallback(
    (rows) => {
      let out = rows;
      if (hasPersonaFilter) {
        out = out.filter((t) => (t.personas || []).some((p) => personaFilters.includes(p)));
      }
      if (hasRoleFilter) {
        out = out.filter((t) => (t.roles || []).some((r) => roleFilters.includes(r)));
      }
      return out;
    },
    [hasPersonaFilter, hasRoleFilter, personaFilters, roleFilters]
  );

  // Scoped to the active persona/role filters, but NOT the free-text search box -
  // this is what the header counts below are based on, so they stay accurate
  // when a facet filter is applied instead of always reflecting the whole catalog.
  const personaRoleFilteredActive = useMemo(
    () => applyPersonaRoleFilter(sorted),
    [sorted, applyPersonaRoleFilter]
  );

  const filtered = useMemo(() => {
    let rows = personaRoleFilteredActive;
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((t) => (t.name || '').toLowerCase().includes(q));
    }
    return rows;
  }, [personaRoleFilteredActive, search]);

  const filteredSessions = filtered.reduce((s, t) => s + (t.sessions || 0), 0);
  const filteredMinutes = filtered.reduce((s, t) => s + (t.totalMinutesSaved || 0), 0);

  const configuredCount = useMemo(() => {
    const scoped = applyPersonaRoleFilter(tools || []);
    return scoped.filter((t) => t.roiStatus === 'configured').length;
  }, [tools, applyPersonaRoleFilter]);
  const activeCount = personaRoleFilteredActive.length;

  return (
    <div className="roi-dash-panel" data-tour="roi-tools-leaderboard">
      <div className="roi-dash-panel-header">
        <div className="roi-dash-panel-title">
          <Trophy size={16} />
          <div>
            <div className="title">Tools Leaderboard</div>
            <div className="subtitle">
              {activeCount} with sessions
              {configuredCount !== activeCount ? ` of ${configuredCount} configured` : ' configured'}
              {' · '}
              {windowLabel} · current deploy env only
              {onSelectTool ? ' · click a tool for contributor breakdown' : ''}
            </div>
          </div>
        </div>
        <div className="roi-dash-panel-controls">
          <div className="roi-dash-seg-group">
            <SegBtn active={sortMode === 'roi'} onClick={() => setSortMode('roi')}>
              Time saved (ROI)
            </SegBtn>
            <SegBtn active={sortMode === 'sessions'} onClick={() => setSortMode('sessions')}>
              Sessions
            </SegBtn>
            <SegBtn active={sortMode === 'per_session'} onClick={() => setSortMode('per_session')}>
              Min Saved / Sess
            </SegBtn>
          </div>
          {lockedPersona ? (
            <span
              className="garage-facet-trigger has-active"
              title={`${lockedPersona} Admin — scoped to ${lockedPersona} apps & gems only`}
              style={{ cursor: 'default' }}
            >
              Persona: {lockedPersona} 🔒
            </span>
          ) : (
            personaOptions.length > 0 && (
              <FacetDropdown
                facet="persona"
                label="Personas"
                options={personaOptions}
                activeValues={personaFilters}
                onToggle={(_facet, value) => onTogglePersona(value)}
              />
            )
          )}
          {roleOptions.length > 0 && (
            <FacetDropdown
              facet="role"
              label="Roles"
              options={roleOptions}
              activeValues={roleFilters}
              onToggle={(_facet, value) => onToggleRole(value)}
            />
          )}
          <div className="roi-dash-search">
            <Search size={12} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tools..."
            />
          </div>
        </div>
        {(search.trim() || hasPersonaFilter || hasRoleFilter) && (
          <div className="roi-dash-search-agg">
            <strong>{filtered.length}</strong> matching ·{' '}
            <strong className="sessions">{formatNumber(filteredSessions)}</strong> sessions ·{' '}
            <strong className="saved">{formatHrs(filteredMinutes)}</strong> saved
          </div>
        )}
      </div>
      <div className="roi-dash-table-wrap">
        <table className="roi-dash-table">
          <thead>
            <tr>
              <th>Tool</th>
              <th>Type</th>
              <th className="num">Sessions</th>
              <th className="num">Min Saved / Sess</th>
              <th className="num">Time Saved</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 10).map((t) => {
              const rank = sorted.findIndex((x) => x.toolId === t.toolId);
              return (
                <tr
                  key={t.toolId}
                  className={onSelectTool ? 'roi-dash-row-clickable' : ''}
                  onClick={onSelectTool ? () => onSelectTool(t) : undefined}
                  title={onSelectTool ? `View users who contributed to ${t.name}'s ROI` : undefined}
                >
                  <td>
                    <div className="tool-cell">
                      <span className={`rank ${rank < 3 ? 'top' : ''}`}>#{rank + 1}</span>
                      <span className="name">{t.name}</span>
                      {t.defaulted && <span className="defaulted">DEFAULTED</span>}
                      {t.roiStatus === 'unconfigured' && (
                        <span className="unconfigured">UNCONFIGURED</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <TypeBadge type={t.type} />
                  </td>
                  <td className="num sessions">{formatNumber(t.sessions)}</td>
                  <td className="num">
                    {t.minPerSession != null ? `${t.minPerSession}m` : '—'}
                  </td>
                  <td className="num saved">
                    {t.totalMinutesSaved != null ? formatHrs(t.totalMinutesSaved) : '—'}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="roi-dash-empty">
                  {search.trim() || hasPersonaFilter || hasRoleFilter
                    ? 'No tools match your filters.'
                    : 'No configured tools with sessions in this window.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="roi-dash-panel-footer">
        Top {Math.min(filtered.length, 10)} of {filtered.length}
        {idxHint(sortMode)}
      </div>
    </div>
  );
};

const idxHint = (sortMode) => {
  if (sortMode === 'sessions') return ' · sorted by sessions';
  if (sortMode === 'per_session') return ' · sorted by min saved / sess';
  return ' · sorted by time saved';
};

const UsersLeaderboard = ({ users, sortMode, setSortMode, excludeAutomation, onSelectUser, personaFilters, roleFilterLabels }) => {
  const [search, setSearch] = useState('');

  // Same as Usage Top Users: hide REACT_APP_DASHBOARD_EXCLUDED_USER_EMAILS (UI only)
  const visibleUsers = useMemo(() => filterDashboardUsers(users || []), [users]);

  const sorted = useMemo(() => {
    const rows = [...visibleUsers];
    rows.sort((a, b) => {
      if (sortMode === 'tools') return (b.toolsAdopted || 0) - (a.toolsAdopted || 0);
      if (sortMode === 'per_session') {
        const aAvg = a.sessions ? a.timeSavedMin / a.sessions : 0;
        const bAvg = b.sessions ? b.timeSavedMin / b.sessions : 0;
        return bAvg - aAvg;
      }
      return (b.timeSavedMin || 0) - (a.timeSavedMin || 0);
    });
    return rows;
  }, [visibleUsers, sortMode]);

  const filtered = useMemo(() => {
    if (!search.trim()) return sorted;
    const q = search.toLowerCase();
    return sorted.filter((u) => {
      const displayName = resolveUserDisplayName(u);
      return (
        displayName.toLowerCase().includes(q) ||
        (u.userName || '').toLowerCase().includes(q) ||
        (u.userEmail || '').toLowerCase().includes(q)
      );
    });
  }, [sorted, search]);

  const filteredSessions = filtered.reduce((s, u) => s + (u.sessions || 0), 0);
  const filteredMinutes = filtered.reduce((s, u) => s + (u.timeSavedMin || 0), 0);

  return (
    <div className="roi-dash-panel" data-tour="roi-users-leaderboard">
      <div className="roi-dash-panel-header">
        <div className="roi-dash-panel-title">
          <Trophy size={16} />
          <div>
            <div className="title">Top Users</div>
            <div className="subtitle">
              Top by{' '}
              {sortMode === 'time'
                ? 'time saved'
                : sortMode === 'tools'
                  ? 'tools adopted'
                  : 'min saved / sess'}{' '}
              ·{' '}
              {excludeAutomation
                ? 'automation accounts excluded'
                : 'automation accounts included ⚠'}
              {personaFilters && personaFilters.length > 0 ? (
                <>
                  {' · '}
                  <strong>{personaFilters.join(', ')}</strong> persona tools only
                </>
              ) : ''}
              {roleFilterLabels && roleFilterLabels.length > 0 ? (
                <>
                  {' · '}
                  <strong>{roleFilterLabels.join(', ')}</strong> role tools only
                </>
              ) : ''}
              {onSelectUser ? ' · click a user for their tools breakdown' : ''}
            </div>
          </div>
        </div>
        <div className="roi-dash-panel-controls">
          <div className="roi-dash-seg-group">
            <SegBtn active={sortMode === 'time'} onClick={() => setSortMode('time')}>
              Time saved
            </SegBtn>
            <SegBtn active={sortMode === 'tools'} onClick={() => setSortMode('tools')}>
              Tools adopted
            </SegBtn>
            <SegBtn active={sortMode === 'per_session'} onClick={() => setSortMode('per_session')}>
              Min Saved / Sess
            </SegBtn>
          </div>
          <div className="roi-dash-search">
            <Search size={12} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
            />
          </div>
        </div>
        {search.trim() && (
          <div className="roi-dash-search-agg">
            <strong>{filtered.length}</strong> matching ·{' '}
            <strong className="sessions">{formatNumber(filteredSessions)}</strong> sessions ·{' '}
            <strong className="saved">{formatHrs(filteredMinutes)}</strong> saved
          </div>
        )}
      </div>
      <div className="roi-dash-table-wrap">
        <table className="roi-dash-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>User</th>
              <th className="num">
                {sortMode === 'time' ? 'Time' : sortMode === 'tools' ? 'Tools' : 'Min Saved / Sess'}
              </th>
              <th className="num">Sessions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 10).map((u) => {
              const rank = sorted.findIndex((x) => x.userEmail === u.userEmail);
              const avg =
                u.sessions > 0 ? (u.timeSavedMin / u.sessions).toFixed(1) : '0.0';
              const displayName = resolveUserDisplayName(u);
              return (
                <tr
                  key={u.userEmail}
                  className={onSelectUser ? 'roi-dash-row-clickable' : ''}
                  onClick={onSelectUser ? () => onSelectUser(u) : undefined}
                  title={onSelectUser ? `View tools used by ${displayName || u.userEmail}` : undefined}
                >
                  <td>
                    {rank === 0 && <Trophy size={14} className="medal gold" />}
                    {rank === 1 && <Award size={14} className="medal silver" />}
                    {rank === 2 && <Award size={14} className="medal bronze" />}
                    {rank > 2 && <span className="rank">#{rank + 1}</span>}
                  </td>
                  <td>
                    <div className="user-cell">
                      <div className="avatar">
                        {(displayName || u.userEmail || '?')
                          .split(/[\s.@]/)
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((p) => p[0]?.toUpperCase())
                          .join('')}
                      </div>
                      <div>
                        <div className="name">{displayName}</div>
                        <div className="email">{u.userEmail}</div>
                      </div>
                    </div>
                  </td>
                  <td className="num saved">
                    {sortMode === 'time' && formatHrs(u.timeSavedMin)}
                    {sortMode === 'tools' && u.toolsAdopted}
                    {sortMode === 'per_session' && `${avg}m`}
                  </td>
                  <td className="num sessions">{formatNumber(u.sessions)}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="roi-dash-empty">
                  No users match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="roi-dash-panel-footer">
        Showing top {Math.min(filtered.length, 10)} of {filtered.length} users
      </div>
    </div>
  );
};

const AdminInsightsStrip = ({ program, tools, onOpenConfig, personaFilters = [], roleFilters = [] }) => {
  const hasPersonaFilter = personaFilters.length > 0;
  const hasRoleFilter = roleFilters.length > 0;

  // Scope every stat below to the active Persona/Role facet filters (same
  // any-match semantics as the Tools Leaderboard) so this panel doesn't leak
  // unrelated-persona tools into a filtered view (e.g. "defaulted" IT/Product
  // tools showing up while only Sales is selected).
  const scopedTools = useMemo(() => {
    let rows = tools || [];
    if (hasPersonaFilter) {
      rows = rows.filter((t) => (t.personas || []).some((p) => personaFilters.includes(p)));
    }
    if (hasRoleFilter) {
      rows = rows.filter((t) => (t.roles || []).some((r) => roleFilters.includes(r)));
    }
    return rows;
  }, [tools, personaFilters, roleFilters, hasPersonaFilter, hasRoleFilter]);

  const configured = scopedTools.filter((t) => t.roiStatus === 'configured');
  const sortedByRoi = [...configured].sort(
    (a, b) => (b.totalMinutesSaved || 0) - (a.totalMinutesSaved || 0)
  );
  const top3 = sortedByRoi.slice(0, 3);
  const top3Min = top3.reduce((s, t) => s + (t.totalMinutesSaved || 0), 0);
  // program.totalMinutes is a global (unfiltered) figure from a separate
  // endpoint - when a facet filter is active, use the scoped configured
  // tools' own total instead so the percentage stays internally consistent.
  const totalMin = (hasPersonaFilter || hasRoleFilter)
    ? configured.reduce((s, t) => s + (t.totalMinutesSaved || 0), 0)
    : (program?.totalMinutes || 0);
  const top3Pct = totalMin > 0 ? Math.round((top3Min / totalMin) * 100) : 0;
  const defaultedCount = scopedTools.filter((t) => t.defaulted).length;
  const unconfiguredCount = (hasPersonaFilter || hasRoleFilter)
    ? scopedTools.filter((t) => t.roiStatus === 'unconfigured').length
    : (program?.unconfiguredCount ?? 0);

  return (
    <div className="roi-dash-insights">
      <div className="roi-dash-insights-header">
        <Sparkles size={16} />
        <span className="title">Program insights</span>
        <span className="subtitle">· auto-generated observations for leadership review</span>
      </div>
      <div className="roi-dash-insights-grid">
        <div className="insight orange">
          <div className="label">Concentration risk</div>
          <div className="body">
            Top 3 tools account for <strong>{top3Pct}%</strong> of program ROI.
            Diversification of tool adoption is a v2 lever.
          </div>
        </div>
        <div className="insight blue">
          <div className="label">Opportunity gap</div>
          <div className="body">
            Configure remaining tools and drive adoption of high min/session Gems to grow
            declared time saved.
          </div>
        </div>
        <div className="insight amber">
          <div className="label">Data hygiene</div>
          <div className="body">
            <strong>{defaultedCount} tools</strong> use system defaults.{' '}
            <strong>{unconfiguredCount} tools</strong> are unconfigured.{' '}
            {onOpenConfig ? (
              <button type="button" className="link" onClick={onOpenConfig}>
                Review in ROI Config
              </button>
            ) : (
              'Review in Admin › ROI.'
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const GarageRoiDashboardSection = ({
  isAdmin = false,
  onOpenRoiConfig,
  userName = '',
  userRole = null,
  accessPersona = null,
  accessPersonas = [],
}) => {
  const adminPersonaScopes = userRole === 'admin'
    ? []
    : getAdminPersonaScopes(userRole, accessPersonas, accessPersona);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [program, setProgram] = useState(null);
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [myRoi, setMyRoi] = useState(null);
  const [myRoiLoading, setMyRoiLoading] = useState(true);
  const roiRequestRef = useRef(0);
  const myRoiRequestRef = useRef(0);
  const usersRequestRef = useRef(0);
  const [toolSort, setToolSort] = useState('roi');
  const [userSort, setUserSort] = useState('time');
  const [personaFilters, setPersonaFilters] = useState([]);
  const [roleFilters, setRoleFilters] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [unreportedTools, setUnreportedTools] = useState([]);
  const [showDigest, setShowDigest] = useState(false);
  const [reportTool, setReportTool] = useState(null);
  const [reportSource, setReportSource] = useState('manual');
  const [reportSessionId, setReportSessionId] = useState(null);
  const [toast, setToast] = useState(null);
  const [dateRange, setDateRange] = useState('90d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [stagingStartDate, setStagingStartDate] = useState('');
  const [stagingEndDate, setStagingEndDate] = useState('');
  const datePickerRef = useRef(null);

  // Tools Leaderboard row click → per-tool ROI contributors modal
  const [toolUsersModalOpen, setToolUsersModalOpen] = useState(false);
  const [toolUsersTool, setToolUsersTool] = useState(null);
  const [toolUsersRows, setToolUsersRows] = useState([]);
  const [toolUsersLoading, setToolUsersLoading] = useState(false);

  // Top Users row click → per-user tools-used modal (with time saved per tool)
  const [userToolsModalOpen, setUserToolsModalOpen] = useState(false);
  const [userToolsUser, setUserToolsUser] = useState(null);
  const [userToolsRows, setUserToolsRows] = useState([]);
  const [userToolsLoading, setUserToolsLoading] = useState(false);

  const windowLabel = useMemo(
    () => formatWindowLabel(dateRange, customStartDate, customEndDate),
    [dateRange, customStartDate, customEndDate]
  );

  const windowParams = useMemo(() => {
    const params = { window: dateRange };
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      params.start_date = customStartDate;
      params.end_date = customEndDate;
    }
    return params;
  }, [dateRange, customStartDate, customEndDate]);

  // Canonical persona list, same admin-managed source ("Config Management →
  // Persona") that the Discover view's persona filter uses. Deriving this from
  // tools-with-sessions only would silently drop valid personas (e.g. "IT",
  // "Security", "Legal", "Global Service Providers") that currently have no
  // active tool. persona's value === label, so no value/label split needed.
  const [personaOptions, setPersonaOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await garageAPI.getConfigOptions('persona');
        if (!cancelled) {
          setPersonaOptions((opts || []).map((o) => o.label || o.value).filter(Boolean));
        }
      } catch (err) {
        console.error('Failed to load persona options', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Drop any selected persona(s) that are no longer valid (e.g. deactivated)
  useEffect(() => {
    if (personaOptions.length === 0) return;
    setPersonaFilters((prev) => prev.filter((p) => personaOptions.includes(p)));
  }, [personaOptions]);

  const togglePersonaFilter = useCallback((value) => {
    setPersonaFilters((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );
  }, []);

  // Canonical target-role list ({value, label}), same admin-managed source
  // ("Config Management → Tool Metadata - Target Roles") that the Discover
  // view's role filter uses. Deriving this from tools-with-sessions only would
  // silently drop valid roles (e.g. "IT") that currently have no active tool.
  // tool.roles stores the option's `value` (e.g. "BDR"), not its display
  // `label` (e.g. "BDR / SDR"), so we filter/query by value and show the label.
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await garageAPI.getConfigOptions('tool_target_role');
        if (!cancelled) {
          setRoleOptions(
            (opts || [])
              .filter((o) => o.value)
              .map((o) => ({ value: o.value, label: o.label || o.value }))
          );
        }
      } catch (err) {
        console.error('Failed to load target role options', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Drop any selected role(s) that are no longer valid (e.g. deactivated)
  useEffect(() => {
    if (roleOptions.length === 0) return;
    setRoleFilters((prev) => prev.filter((r) => roleOptions.some((opt) => opt.value === r)));
  }, [roleOptions]);

  const toggleRoleFilter = useCallback((value) => {
    setRoleFilters((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
    );
  }, []);

  const roleFilterLabels = useMemo(
    () => roleFilters.map((v) => roleOptions.find((o) => o.value === v)?.label || v),
    [roleFilters, roleOptions]
  );

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3200);
  };

  const applyPresetRange = (range) => {
    setDateRange(range);
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomPicker(false);
  };

  const applyCustomRange = () => {
    if (!stagingStartDate || !stagingEndDate) return;
    let start = stagingStartDate;
    let end = stagingEndDate;
    if (end < start) {
      [start, end] = [end, start];
    }
    setCustomStartDate(start);
    setCustomEndDate(end);
    setDateRange('custom');
    setShowCustomPicker(false);
  };

  const loadRoi = useCallback(async () => {
    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
      return;
    }
    const requestId = ++roiRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      if (isAdmin) {
        const [programData, toolsData, unreported] = await Promise.all([
          garageAPI.getRoiProgram(windowParams),
          garageAPI.getRoiTools(windowParams),
          garageAPI.getRoiUnreported().catch(() => null),
        ]);
        if (requestId !== roiRequestRef.current) return;
        setProgram(programData);
        setTools(toolsData || []);
        setUnreportedTools(unreported?.tools || []);
        if (unreported?.shouldShowDigest) {
          setShowDigest(true);
        }
      } else {
        // Time Saved is the primary card here (loaded by loadMyRoi), so a failing
        // leaderboard degrades to an empty list instead of replacing the whole
        // section with an error.
        const [toolsData, unreported] = await Promise.all([
          garageAPI.getRoiTools({ ...windowParams, status: 'configured' }).catch(() => []),
          garageAPI.getRoiUnreported().catch(() => null),
        ]);
        if (requestId !== roiRequestRef.current) return;
        setTools(toolsData || []);
        setUnreportedTools(unreported?.tools || []);
        if (unreported?.shouldShowDigest) {
          setShowDigest(true);
        }
      }
    } catch (err) {
      if (requestId !== roiRequestRef.current) return;
      console.error('Failed to load ROI dashboard section', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load ROI data');
    } finally {
      if (requestId === roiRequestRef.current) setLoading(false);
    }
  }, [isAdmin, windowParams, dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    loadRoi();
    return () => { roiRequestRef.current += 1; };
  }, [loadRoi]);

  // "My ROI" (time saved / sessions / tools adopted / rank) is fetched apart from
  // the rest of the dashboard so the Persona/Role facets can re-scope just this
  // card without reloading everything. It owns the fetch outright — when loadRoi
  // also fetched it, the unscoped response raced the facet-scoped one.
  const loadMyRoi = useCallback(async () => {
    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
      return;
    }
    const requestId = ++myRoiRequestRef.current;
    setMyRoiLoading(true);
    try {
      const myData = await garageAPI.getMyRoi({
        ...windowParams,
        ...(personaFilters.length > 0 ? { personas: personaFilters.join(',') } : {}),
        ...(roleFilters.length > 0 ? { roles: roleFilters.join(',') } : {}),
      });
      if (requestId !== myRoiRequestRef.current) return;
      setMyRoi(myData);
    } catch (err) {
      if (requestId !== myRoiRequestRef.current) return;
      console.error('Failed to load My ROI', err);
      if (!isAdmin) {
        setError(err.response?.data?.detail || err.message || 'Failed to load ROI data');
      }
    } finally {
      if (requestId === myRoiRequestRef.current) setMyRoiLoading(false);
    }
  }, [isAdmin, windowParams, dateRange, customStartDate, customEndDate, personaFilters, roleFilters]);

  useEffect(() => {
    loadMyRoi();
    return () => { myRoiRequestRef.current += 1; };
  }, [loadMyRoi]);

  // Top Users leaderboard is fetched separately so changing the persona filter
  // only re-aggregates users (server-side), without reloading the hero/tools data.
  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) return;
    const requestId = ++usersRequestRef.current;
    setUsersLoading(true);
    try {
      const usersData = await garageAPI.getRoiUsers({
        ...windowParams,
        limit: 50,
        ...(personaFilters.length > 0 ? { personas: personaFilters.join(',') } : {}),
        ...(roleFilters.length > 0 ? { roles: roleFilters.join(',') } : {}),
      });
      if (requestId !== usersRequestRef.current) return;
      setUsers(usersData || []);
    } catch (err) {
      if (requestId !== usersRequestRef.current) return;
      console.error('Failed to load ROI users', err);
      showToast(err.response?.data?.detail || 'Could not load Top Users');
    } finally {
      if (requestId === usersRequestRef.current) setUsersLoading(false);
    }
  }, [isAdmin, windowParams, personaFilters, roleFilters, dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    loadUsers();
    return () => { usersRequestRef.current += 1; };
  }, [loadUsers]);

  useEffect(() => {
    if (!showCustomPicker) return undefined;
    const onDocClick = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowCustomPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showCustomPicker]);

  // Post-session: when user returns to Garage after launching a tool
  useEffect(() => {
    const maybeOpenPending = () => {
      try {
        const raw = localStorage.getItem(PENDING_REPORT_KEY);
        if (!raw) return;
        const pending = JSON.parse(raw);
        if (!pending?.toolId || !pending?.name) {
          localStorage.removeItem(PENDING_REPORT_KEY);
          return;
        }
        // Only prompt if launched recently (2 hours)
        if (pending.at && Date.now() - pending.at > 2 * 60 * 60 * 1000) {
          localStorage.removeItem(PENDING_REPORT_KEY);
          return;
        }
        setReportTool({
          toolId: pending.toolId,
          name: pending.name,
          adminEstimate: pending.adminEstimate ?? null,
        });
        setReportSource('post_session');
        setReportSessionId(pending.sessionId || null);
        localStorage.removeItem(PENDING_REPORT_KEY);
      } catch (_) {
        localStorage.removeItem(PENDING_REPORT_KEY);
      }
    };

    maybeOpenPending();
    window.addEventListener('focus', maybeOpenPending);
    return () => window.removeEventListener('focus', maybeOpenPending);
  }, []);

  const openToolUsers = async (tool) => {
    setToolUsersTool(tool);
    setToolUsersModalOpen(true);
    setToolUsersLoading(true);
    setToolUsersRows([]);
    try {
      const rows = await garageAPI.getRoiToolUsers(tool.toolId, windowParams);
      setToolUsersRows(rows || []);
    } catch (err) {
      console.error('Failed to load tool ROI contributors', err);
      showToast(err.response?.data?.detail || 'Could not load contributors for this tool');
    } finally {
      setToolUsersLoading(false);
    }
  };

  const closeToolUsers = () => {
    setToolUsersModalOpen(false);
    setToolUsersTool(null);
    setToolUsersRows([]);
  };

  const openUserTools = async (user) => {
    const displayName = resolveUserDisplayName(user) || user.userEmail;
    setUserToolsUser({
      userEmail: user.userEmail,
      userName: displayName,
      sessions: user.sessions,
      toolsUsed: user.toolsAdopted,
      timeSavedMin: user.timeSavedMin,
    });
    setUserToolsModalOpen(true);
    setUserToolsLoading(true);
    setUserToolsRows([]);
    try {
      const rows = await garageAPI.getRoiUserTools(user.userEmail, {
        ...windowParams,
        ...(personaFilters.length > 0 ? { personas: personaFilters.join(',') } : {}),
        ...(roleFilters.length > 0 ? { roles: roleFilters.join(',') } : {}),
      });
      setUserToolsRows(rows || []);
    } catch (err) {
      console.error('Failed to load user ROI tools', err);
      showToast(err.response?.data?.detail || "Could not load this user's tools");
    } finally {
      setUserToolsLoading(false);
    }
  };

  const closeUserTools = () => {
    setUserToolsModalOpen(false);
    setUserToolsUser(null);
    setUserToolsRows([]);
  };

  const openDigest = async () => {
    try {
      const data = await garageAPI.getRoiUnreported();
      setUnreportedTools(data?.tools || []);
      if ((data?.tools || []).length === 0) {
        showToast('No unreported sessions right now');
        return;
      }
      setShowDigest(true);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not load unreported sessions');
    }
  };

  if ((loading && !program && tools.length === 0) || (!isAdmin && myRoiLoading && !myRoi)) {
    return <GarageLoadingScreen message="Loading Time Saved…" />;
  }

  if (error && !program && tools.length === 0) {
    return (
      <div className="roi-dash-error">
        <span>{error}</span>
        <button type="button" onClick={loadRoi}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="roi-dash">
      {adminPersonaScopes.length > 0 && (
        <div style={{
          padding: '10px 16px',
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#1565c0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Trophy size={16} />
          <span>
            <strong>{adminPersonaScopes.join(', ')} Admin View:</strong> Showing Time Saved (ROI) for assigned-persona apps &amp; gems only.
          </span>
        </div>
      )}
      <div className="roi-dash-filters">
        <div className="roi-dash-filters-label">
          <Calendar size={14} />
          <span>Date range</span>
        </div>
        <div className="roi-dash-filters-btns">
          {['7d', '30d', '90d', 'all'].map((range) => (
            <button
              key={range}
              type="button"
              className={`roi-dash-date-btn ${dateRange === range ? 'active' : ''}`}
              onClick={() => applyPresetRange(range)}
            >
              {WINDOW_LABELS[range]}
            </button>
          ))}
          <div className="roi-dash-custom-wrap" ref={datePickerRef}>
            <button
              type="button"
              className={`roi-dash-date-btn ${dateRange === 'custom' ? 'active' : ''}`}
              onClick={() => {
                setStagingStartDate(customStartDate || '');
                setStagingEndDate(customEndDate || '');
                setShowCustomPicker((v) => !v);
              }}
            >
              <Calendar size={13} />
              Custom
            </button>
            {showCustomPicker && (
              <div className="roi-dash-custom-panel" onClick={(e) => e.stopPropagation()}>
                <label>
                  Start
                  <input
                    type="date"
                    value={stagingStartDate}
                    onChange={(e) => setStagingStartDate(e.target.value)}
                  />
                </label>
                <label>
                  End
                  <input
                    type="date"
                    value={stagingEndDate}
                    onChange={(e) => setStagingEndDate(e.target.value)}
                  />
                </label>
                <div className="roi-dash-custom-actions">
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => {
                      setShowCustomPicker(false);
                      if (dateRange === 'custom') {
                        applyPresetRange('90d');
                      }
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={!stagingStartDate || !stagingEndDate}
                    onClick={applyCustomRange}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {(loading || usersLoading || myRoiLoading) && (
          <GarageLoadingScreen compact message="Updating…" />
        )}
      </div>

      {/* Prototype/BRD role split: AdminHero XOR UserHero — never both */}
      <GarageLoadingRegion loading={isAdmin ? loading : myRoiLoading} showIndicator={false}>
      {isAdmin ? (
        <AdminHero program={program} windowLabel={windowLabel} />
      ) : (
        <UserHero myRoi={myRoi} windowLabel={windowLabel} onOpenDigest={openDigest} />
      )}

      </GarageLoadingRegion>

      <GarageLoadingRegion
        loading={loading || (isAdmin ? usersLoading : myRoiLoading)}
        showIndicator={false}
      >
      <div className={`roi-dash-boards ${isAdmin ? 'admin' : 'user'}`}>
        <ToolsLeaderboard
          tools={tools}
          sortMode={toolSort}
          setSortMode={setToolSort}
          windowLabel={windowLabel}
          onSelectTool={isAdmin ? openToolUsers : undefined}
          personaFilters={personaFilters}
          onTogglePersona={togglePersonaFilter}
          personaOptions={adminPersonaScopes.length ? adminPersonaScopes : personaOptions}
          roleFilters={roleFilters}
          onToggleRole={toggleRoleFilter}
          roleOptions={roleOptions}
          lockedPersona={
            adminPersonaScopes.length === 1 ? adminPersonaScopes[0] : null
          }
        />
        {isAdmin ? (
          <UsersLeaderboard
            users={users}
            sortMode={userSort}
            setSortMode={setUserSort}
            excludeAutomation={program?.excludeAutomation}
            onSelectUser={openUserTools}
            personaFilters={personaFilters}
            roleFilterLabels={roleFilterLabels}
          />
        ) : (
          <UserTopTools
            myRoi={myRoi}
            windowLabel={windowLabel}
            onReportTool={(t) => {
              setReportTool(t);
              setReportSource('manual');
              setReportSessionId(null);
            }}
          />
        )}
      </div>

      {isAdmin && (
        <AdminInsightsStrip
          program={program}
          tools={tools}
          onOpenConfig={onOpenRoiConfig}
          personaFilters={personaFilters}
          roleFilters={roleFilters}
        />
      )}

      </GarageLoadingRegion>
      {/* Prompts stay available for all roles (auto-digest + post-session) without changing admin layout */}
      <GarageRoiDigestModal
        open={showDigest}
        tools={unreportedTools}
        userName={userName}
        onClose={() => setShowDigest(false)}
        onSaved={({ saved, snoozed }) => {
          if (snoozed) showToast(`Prompts snoozed (${snoozed.replace('_', ' ')})`);
          else showToast(`${saved || 0} report(s) saved — thank you`);
          loadRoi();
        }}
      />
      <GarageRoiReportModal
        open={!!reportTool}
        tool={reportTool}
        source={reportSource}
        sessionId={reportSessionId}
        onClose={() => setReportTool(null)}
        onSaved={() => {
          showToast('Time-saved report saved');
          loadRoi();
        }}
      />

      <ToolRoiUsersModal
        isOpen={toolUsersModalOpen}
        tool={toolUsersTool}
        users={toolUsersRows}
        loading={toolUsersLoading}
        windowLabel={windowLabel}
        onClose={closeToolUsers}
      />

      <UserToolsModal
        isOpen={userToolsModalOpen}
        user={userToolsUser}
        userTools={userToolsRows}
        loading={userToolsLoading}
        onClose={closeUserTools}
        getUserInitials={getUserInitials}
        getUserColor={getUserColor}
      />

      {toast && <div className="roi-dash-toast">{toast}</div>}
    </div>
  );
};

export default GarageRoiDashboardSection;
